import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  FeedbackOrderInfo,
  getFeedbackInfo,
  autosaveFeedback,
  submitFeedback,
  AutosavePayload,
} from "@/features/delivery/api/feedbackApi";

interface StarRatingProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
}

function StarRating({ label, value, onChange }: StarRatingProps) {
  return (
    <div>
      <span className="mb-1 block text-sm font-medium text-neutral-800">{label}</span>
      <div className="flex gap-1" role="radiogroup" aria-label={label}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={value === star}
            aria-label={`${star} star${star > 1 ? "s" : ""}`}
            onClick={() => onChange(star)}
            className="p-1"
          >
            <Star
              className={`h-7 w-7 ${star <= value ? "fill-amber-400 text-amber-400" : "text-neutral-300"}`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

const TIP_PRESETS = [5, 10, 20, 50];

export default function DeliveryFeedback() {
  const { token = "" } = useParams();
  const [info, setInfo] = useState<FeedbackOrderInfo | null>(null);
  const [loadError, setLoadError] = useState("");
  const [handover, setHandover] = useState(0);
  const [attitude, setAttitude] = useState(0);
  const [explanation, setExplanation] = useState(0);
  const [comment, setComment] = useState("");
  const [tipAmount, setTipAmount] = useState<number>(0);
  const [customTip, setCustomTip] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    getFeedbackInfo(token)
      .then((result) => {
        setInfo(result);
        setSubmitted(result.submitted);
        // Restore saved values if any
        if (result.feedback) {
          if (result.feedback.handover_rating) setHandover(result.feedback.handover_rating);
          if (result.feedback.attitude_rating) setAttitude(result.feedback.attitude_rating);
          if (result.feedback.explanation_rating) setExplanation(result.feedback.explanation_rating);
          if (result.feedback.comment) setComment(result.feedback.comment);
          if (result.feedback.tip_amount) {
            const tip = Number(result.feedback.tip_amount);
            setTipAmount(tip);
            if (!TIP_PRESETS.includes(tip) && tip > 0) {
              setCustomTip(String(tip));
            }
          }
        }
      })
      .catch(() => setLoadError("This feedback link is invalid or has expired."));
  }, [token]);

  // Auto-save debounced
  const doAutosave = useCallback(
    (payload: AutosavePayload) => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
      autosaveTimer.current = setTimeout(() => {
        setSaving(true);
        autosaveFeedback(token, payload)
          .finally(() => setSaving(false));
      }, 800);
    },
    [token]
  );

  // When any field changes, trigger auto-save
  const handleFieldChange = useCallback(
    (field: keyof AutosavePayload, value: number | string) => {
      const payload: AutosavePayload = {};

      if (field === "handover_rating") { setHandover(value as number); payload.handover_rating = value as number; }
      else if (field === "attitude_rating") { setAttitude(value as number); payload.attitude_rating = value as number; }
      else if (field === "explanation_rating") { setExplanation(value as number); payload.explanation_rating = value as number; }
      else if (field === "comment") { setComment(value as string); payload.comment = value as string; }
      else if (field === "tip_amount") { setTipAmount(value as number); payload.tip_amount = value as number; }

      doAutosave(payload);
    },
    [doAutosave]
  );

  const handleTipPreset = (amount: number) => {
    setCustomTip("");
    handleFieldChange("tip_amount", amount);
  };

  const handleCustomTipChange = (value: string) => {
    setCustomTip(value);
    const num = parseFloat(value) || 0;
    setTipAmount(num);
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      setSaving(true);
      autosaveFeedback(token, { tip_amount: num })
        .finally(() => setSaving(false));
    }, 800);
  };

  const canSubmit = handover > 0 && attitude > 0 && explanation > 0 && !submitting;

  const onSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const result = await submitFeedback(token, {
        handover_rating: handover,
        attitude_rating: attitude,
        explanation_rating: explanation,
        comment: comment.trim() || undefined,
        tip_amount: tipAmount > 0 ? tipAmount : undefined,
      });

      // If tip payment URL returned, redirect to Stripe
      if (result.tip_payment_url) {
        window.location.href = result.tip_payment_url;
        return;
      }

      setSubmitted(true);
    } catch (err: any) {
      setError(err?.message ?? "Sorry, we couldn't save your feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-neutral-50 p-4">
        <p className="rounded-md bg-white p-6 text-center text-sm text-neutral-600 shadow-sm">{loadError}</p>
      </main>
    );
  }

  if (submitted) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-neutral-50 p-4">
        <div className="max-w-md rounded-md bg-white p-8 text-center shadow-sm" data-testid="feedback-thanks">
          <h1 className="text-xl font-semibold text-neutral-900">Thank you!</h1>
          <p className="mt-2 text-sm text-neutral-600">Your feedback helps us improve our delivery service.</p>
          {tipAmount > 0 && info?.feedback?.tip_payment_status !== "paid" && (
            <p className="mt-2 text-sm text-green-700">Your tip is being processed. Thank you for your generosity!</p>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen justify-center bg-neutral-50 p-4">
      <div className="w-full max-w-md space-y-6 rounded-md bg-white p-6 shadow-sm">
        <header>
          <h1 className="text-xl font-semibold text-neutral-900">How was your delivery?</h1>
          <p className="mt-1 text-sm text-neutral-600">
            {info?.order?.driver_name
              ? `Your ${info.order.type === "return" ? "car collection" : "car delivery"} was handled by ${info.order.driver_name}.`
              : "Please rate your recent Europcar delivery."}
          </p>
          {info?.order && (
            <div className="mt-3 rounded-md bg-neutral-50 p-3 text-sm text-neutral-700">
              <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                <dt className="text-neutral-500">Service:</dt>
                <dd className="font-medium">{info.order.type === "return" ? "Car collection" : "Car delivery"}</dd>
                {info.order.scheduled_at && (
                  <>
                    <dt className="text-neutral-500">Date:</dt>
                    <dd>{new Date(info.order.scheduled_at).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}</dd>
                  </>
                )}
                {info.order.vehicle_model && (
                  <>
                    <dt className="text-neutral-500">Vehicle:</dt>
                    <dd>{info.order.vehicle_model}{info.order.vehicle_plate ? ` — ${info.order.vehicle_plate}` : ""}</dd>
                  </>
                )}
              </dl>
            </div>
          )}
        </header>

        {error && <p className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</p>}

        <StarRating label="Vehicle handover" value={handover} onChange={(v) => handleFieldChange("handover_rating", v)} />
        <StarRating label="Driver's attitude" value={attitude} onChange={(v) => handleFieldChange("attitude_rating", v)} />
        <StarRating label="Explanation of the vehicle & rental" value={explanation} onChange={(v) => handleFieldChange("explanation_rating", v)} />

        <div>
          <label htmlFor="comment" className="mb-1 block text-sm font-medium text-neutral-800">
            Anything else? (optional)
          </label>
          <textarea
            id="comment"
            value={comment}
            onChange={(e) => handleFieldChange("comment", e.target.value)}
            rows={4}
            maxLength={2000}
            className="w-full rounded-md border border-neutral-300 p-2 text-sm"
            placeholder="Tell us more about your experience"
          />
        </div>

        {/* Tip section */}
        <div className="border-t pt-4">
          <span className="mb-2 block text-sm font-medium text-neutral-800">
            Would you like to tip your driver? (optional)
          </span>
          <div className="flex flex-wrap gap-2">
            {TIP_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => handleTipPreset(preset)}
                className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                  tipAmount === preset && !customTip
                    ? "border-green-600 bg-green-50 text-green-700"
                    : "border-neutral-300 text-neutral-700 hover:border-green-400"
                }`}
              >
                RM {preset}
              </button>
            ))}
            <button
              type="button"
              onClick={() => { setTipAmount(0); setCustomTip(""); handleFieldChange("tip_amount", 0); }}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                tipAmount === 0
                  ? "border-neutral-600 bg-neutral-100 text-neutral-700"
                  : "border-neutral-300 text-neutral-500 hover:border-neutral-400"
              }`}
            >
              No tip
            </button>
          </div>
          <div className="mt-3">
            <label htmlFor="custom-tip" className="text-xs text-neutral-500">Or enter custom amount:</label>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-sm text-neutral-600">RM</span>
              <input
                id="custom-tip"
                type="number"
                min="0"
                step="1"
                value={customTip}
                onChange={(e) => handleCustomTipChange(e.target.value)}
                className="w-24 rounded-md border border-neutral-300 p-2 text-sm"
                placeholder="0"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <Button className="flex-1" disabled={!canSubmit} onClick={onSubmit}>
            {submitting ? "Submitting..." : tipAmount > 0 ? `Submit & Pay RM ${tipAmount} Tip` : "Submit feedback"}
          </Button>
          {saving && <span className="ml-3 text-xs text-neutral-400">Saving...</span>}
        </div>
      </div>
    </main>
  );
}
